import { Component, ViewChild, OnDestroy } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { Inject } from '@angular/core';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { base64ToFile } from '../../utils';

export interface CropImageBottomSheetData {
  imageChangedEvent: any;
  originalFile: File;
}

export interface CropImageBottomSheetResult {
  croppedFile: File;
}

@Component({
  selector: 'app-crop-image-bottom-sheet',
  templateUrl: './crop-image-bottom-sheet.component.html',
  styleUrls: ['./crop-image-bottom-sheet.component.scss'],
  standalone: false
})
export class CropImageBottomSheetComponent implements OnDestroy {
  @ViewChild('previewImage') previewImage: ImageCropperComponent;

  croppedImage: string | null = null;
  canvasRotation = 0;
  transform: ImageTransform = {};
  loading = true;

  constructor(
    private readonly bottomSheetRef: MatBottomSheetRef<CropImageBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: CropImageBottomSheetData
  ) {}

  ngOnDestroy(): void {}

  onImageCropped(event: ImageCroppedEvent): void {
    if (event?.base64) {
      this.croppedImage = event.base64;
    }
  }

  onImageLoaded(): void {
    this.loading = false;
  }

  onCropperReady(): void {
    this.loading = false;
  }

  onLoadImageFailed(): void {
    this.loading = false;
  }

  rotateLeft(): void {
    this.canvasRotation--;
    this.flipAfterRotate();
  }

  rotateRight(): void {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  private flipAfterRotate(): void {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH
    };
  }

  async onFinish(): Promise<void> {
    if (!this.croppedImage && this.previewImage) {
      const cropPromise = new Promise<void>(resolve => {
        const sub = this.previewImage.imageCropped.subscribe((event: ImageCroppedEvent) => {
          if (event?.base64) {
            this.croppedImage = event.base64;
          }
          sub.unsubscribe();
          resolve();
        });
        this.previewImage.crop();
      });
      await cropPromise;
    }

    if (this.croppedImage) {
      const croppedFile = new File(
        [base64ToFile(this.croppedImage)],
        this.data.originalFile.name,
        { type: this.data.originalFile.type }
      );
      const result: CropImageBottomSheetResult = { croppedFile };
      this.bottomSheetRef.dismiss(result);
    } else {
      // No crop happened — return original file
      this.bottomSheetRef.dismiss({ croppedFile: this.data.originalFile });
    }
  }

  onCancel(): void {
    this.bottomSheetRef.dismiss(null);
  }
}
